import { AgentControl } from "type/agent-control";

/**
 * The three form fields the agent block owns. Deliberately not either form's `FormState` — both
 * are non-exported, and taking a narrow input means the helper has no dependency on either form's
 * shape and a test can construct its input in one line.
 */
export interface AgentFieldsInput {
	agentId: number;
	agentName: string;
	showOnAgentPage: boolean;
}

/** Every key optional: an absent key is how the payload says "leave this alone". */
export interface AgentFields {
	agentId?: number;
	agentName?: string;
	showOnAgentPage?: boolean;
}

/**
 * MAX-4194. Builds the agent portion of a supplemental or off-market save payload.
 *
 * The contract is **absent preserves, present acts** — see
 * `ClientPropertyResourceTranslator.applyAgent` on the server, which only touches a column when
 * its key is in the body. Before this ticket both forms sent all four agent keys unconditionally,
 * so every routine save re-entered `applyAgent` and zeroed `AID`, silently unassigning the agent
 * on roughly 460 rows.
 *
 * `baseline` is what the agent box was *initialised with* — the stored name, or the AID-resolved
 * seed from Step 9 — captured alongside the form state and never recomputed. It is a string
 * rather than the loaded record on purpose: on a seeded row the server's `agentName` is `""`
 * while the box holds the agent's name, so comparing against the record would read every
 * untouched save as a rename and send nothing at all for a real clear. The comparison has to be
 * against what the user was actually shown, which the record alone cannot say.
 *
 * `null` baseline means the create path.
 *
 * `agentControl` is passed in rather than re-derived, so the mode is decided in exactly one place
 * per render.
 *
 * `agentCode` is never emitted. The form never populates it, and omitting it is what stops the
 * unconditional null that hits every account today — confirmed safe against prod by the Step 15a
 * probe. Its absence is also why one helper can serve both forms: it was the only field the two
 * payloads differed on, so the result is assignable into either `Upsert` type.
 *
 * MAX-4194: guarded by shared/agent-fields.test.ts; CI skips tests — run locally before editing.
 */
export function buildAgentFields(
	form: AgentFieldsInput,
	agentControl: AgentControl,
	baseline: string | null,
): AgentFields {
	// Trim before deciding, and send the trimmed value. Two consequences, because Step 7's
	// server-side rule reads as though whitespace is always inert: replacing a stored name with
	// spaces IS a clear here, and a set never stores leading or trailing spaces. The server's
	// isEmpty() test exists solely for the previously deployed bundle, which passes "  " through
	// untrimmed; this one never sends whitespace at all.
	const typed = form.agentName.trim();

	if (baseline === null) {
		// Create. Values are spelled out rather than passed through, because the form's
		// new-listing default is `showOnAgentPage: true` — forwarding `form.showOnAgentPage`
		// would flip every new listing on a non-dropdown account to ShowOnAgentPage = 1.
		return {
			agentId: agentControl === "dropdown" ? form.agentId : 0,
			showOnAgentPage: agentControl === "dropdown" ? form.showOnAgentPage : false,
			...(agentControl === "freeText" && typed !== "" ? { agentName: typed } : {}),
		};
	}

	// Update.
	if (agentControl === "dropdown") {
		// A stale typed name is left alone deliberately — the dropdown does not own that column,
		// and rewriting it here would destroy a name the user cannot see or edit in this mode.
		return { agentId: form.agentId, showOnAgentPage: form.showOnAgentPage };
	}

	if (agentControl === "freeText") {
		const before = baseline.trim();
		// Both branches are dirty-gated, and both pair the name with `agentId: 0`.
		//
		// The pairing is load-bearing, not incidental: both public display paths resolve the
		// agent from AID *ahead of* the typed name (v9 ListingController:1620-1628, v10 kestrel
		// ListingResourceTranslator:121-123). Omitting `agentId` while the server's Step 6 guard
		// is in place would write the new name and leave a stale AID, so the user would type a
		// name, save successfully, and still see the old agent — with no affordance in free-text
		// mode to fix it.
		//
		// A clear is just a rename to nothing, which is why the two branches look alike.
		if (typed !== "" && typed !== before) {
			return { agentName: typed, agentId: 0 };
		}
		if (typed === "" && before !== "") {
			return { agentName: "", agentId: 0 };
		}
		// Unchanged: send nothing about the agent at all, so `applyAgent` is never entered and
		// AID, the name columns, AGT_CODE and ShowOnAgentPage are all preserved. Stronger than
		// re-sending the name unchanged, which would rewrite the columns on every save.
		//
		// Never `showOnAgentPage` in free-text mode — that control is not rendered here.
		return {};
	}

	// "none" — no agent control is rendered, so the save says nothing about the agent.
	return {};
}
